import React, { useEffect, useMemo, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import {
  MessageCircle, Users, Cake, Star, Clock, Send, Eye, ExternalLink,
  History, CheckCircle, AlertCircle, X, Search, Settings as SettingsIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

/**
 * WhatsApp Campaigns — broadcast to customer segments.
 *
 * Flow:
 *   1. Pick a segment (audience). Server tells us how many recipients match.
 *   2. Write a message body with {placeholders}.
 *   3. Preview → server renders 3 sample messages.
 *   4. Send → server dispatches via stub (wa.me links) or Meta API based on
 *      the tenant's WhatsApp settings, and records the campaign + per-recipient
 *      delivery log for audit.
 */

const SEGMENT_META = [
  { id: "all", icon: Users, color: "text-blue-600 bg-blue-50",
    label_ar: "كل العملاء", label_en: "All customers",
    desc_ar: "كل العملاء الذين لديهم رقم جوال", desc_en: "Every customer with a phone" },
  { id: "with_phone", icon: Users, color: "text-blue-600 bg-blue-50",
    label_ar: "أصحاب أرقام الجوال", label_en: "With phone number",
    desc_ar: "العملاء المسجل لهم رقم تواصل", desc_en: "Customers reachable on WhatsApp" },
  { id: "inactive_30", icon: Clock, color: "text-amber-600 bg-amber-50",
    label_ar: "خاملون 30 يوم", label_en: "Inactive 30 days",
    desc_ar: "لم يحجزوا منذ شهر — استرجعهم", desc_en: "No booking in 30 days — win them back" },
  { id: "inactive_60", icon: Clock, color: "text-amber-600 bg-amber-50",
    label_ar: "خاملون 60 يوم", label_en: "Inactive 60 days",
    desc_ar: "لم يحجزوا منذ شهرين", desc_en: "No booking in 60 days" },
  { id: "inactive_90", icon: Clock, color: "text-rose-600 bg-rose-50",
    label_ar: "خاملون 90 يوم", label_en: "Inactive 90 days",
    desc_ar: "خطر فقدانهم — كن استباقياً", desc_en: "Risk of churn — be proactive" },
  { id: "birthday_week", icon: Cake, color: "text-pink-600 bg-pink-50",
    label_ar: "أعياد الميلاد هذا الأسبوع", label_en: "Birthdays this week",
    desc_ar: "هدية أو خصم في يومهم", desc_en: "Send a gift or birthday discount" },
  { id: "birthday_month", icon: Cake, color: "text-pink-600 bg-pink-50",
    label_ar: "أعياد الميلاد هذا الشهر", label_en: "Birthdays this month",
    desc_ar: "خصومات طوال الشهر", desc_en: "Whole-month birthday promo" },
  { id: "vip_top10", icon: Star, color: "text-yellow-600 bg-yellow-50",
    label_ar: "أفضل 10 عملاء", label_en: "Top 10 spenders",
    desc_ar: "الأكثر إنفاقاً — اهتم بهم", desc_en: "Treat your most valuable clients" },
  { id: "vip_high_spend", icon: Star, color: "text-yellow-600 bg-yellow-50",
    label_ar: "VIP (إنفاق ≥ 500)", label_en: "VIP (spend ≥ 500)",
    desc_ar: "عملاء عاليي القيمة", desc_en: "High-value customers" },
];

const PLACEHOLDERS = [
  { tag: "{name}", desc_ar: "اسم العميل", desc_en: "Customer name" },
  { tag: "{phone}", desc_ar: "رقم الجوال", desc_en: "Phone" },
  { tag: "{points}", desc_ar: "نقاط الولاء", desc_en: "Loyalty points" },
  { tag: "{tier}", desc_ar: "فئة العضوية", desc_en: "Membership tier" },
  { tag: "{spent}", desc_ar: "إجمالي الإنفاق", desc_en: "Total spent" },
];

const PRESETS = [
  { id: "winback",  label_ar: "استرجاع",     label_en: "Win back",
    body_ar: "مرحباً {name}! نفتقدك في صالوننا 💛 سعداء برؤيتك مجدداً — احصل على خصم 15% على زيارتك القادمة.",
    body_en: "Hi {name}! We miss you 💛 Enjoy 15% off your next visit." },
  { id: "birthday", label_ar: "عيد ميلاد",   label_en: "Birthday",
    body_ar: "كل عام وأنت بخير {name} 🎂 هدية يوم ميلادك بانتظارك — مرحباً بك في الصالون.",
    body_en: "Happy Birthday {name} 🎂 A special gift is waiting for you." },
  { id: "vip",      label_ar: "كبار العملاء", label_en: "VIP",
    body_ar: "عزيزتنا {name}، شكراً لاختياركِ لنا. كفئة {tier} لديكِ {points} نقطة جاهزة للاستبدال ✨",
    body_en: "Dear {name}, thank you for choosing us. As {tier} you have {points} points ready to redeem ✨" },
  { id: "promo",    label_ar: "عرض جديد",    label_en: "New promo",
    body_ar: "تنبيه {name} 🌹 عروض جديدة هذا الأسبوع! تواصلي معنا للحجز.",
    body_en: "Hey {name} 🌹 New offers this week! Book now." },
];

export default function WhatsAppCampaigns() {
  const { lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const t = (en, ar) => (lang === "ar" ? ar : en);

  const [segments, setSegments] = useState({});
  const [status, setStatus] = useState(null);
  const [segment, setSegment] = useState("inactive_30");
  const [campaignName, setCampaignName] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const refresh = async () => {
    try {
      const [s, st, h] = await Promise.all([
        api.get("/whatsapp/campaigns/segments"),
        api.get("/whatsapp/status"),
        api.get("/whatsapp/campaigns/history?limit=10"),
      ]);
      setSegments(s.data.segments || {});
      setStatus(st.data);
      setHistory(h.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const doPreview = async () => {
    if (!body.trim()) return toast.error(t("Write a message first", "اكتب الرسالة أولاً"));
    setPreviewing(true);
    try {
      const r = await api.post("/whatsapp/campaigns/preview", { segment, body });
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setPreviewing(false); }
  };

  const doSend = async (dryRun = false) => {
    if (!body.trim()) return toast.error(t("Write a message first", "اكتب الرسالة أولاً"));
    if (!dryRun) {
      const ok = await confirm({
        title: lang === "ar"
          ? `هل أنت متأكد من إرسال الحملة إلى ${segments[segment] || 0} عميل؟`
          : `Send this campaign to ${segments[segment] || 0} customers?`,
        confirmLabel: lang === "ar" ? "إرسال" : "Send",
        cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
        variant: "danger",
      });
      if (!ok) return;
    }
    setSending(true);
    try {
      const r = await api.post("/whatsapp/campaigns/send", {
        segment, body, name: campaignName || `${segment} broadcast`, dry_run: dryRun,
      });
      setResult(r.data);
      if (!dryRun) {
        toast.success(t(`Sent ${r.data.success_count}/${r.data.total}`, `تم إرسال ${r.data.success_count}/${r.data.total}`));
        refresh();
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setSending(false); }
  };

  const insertPlaceholder = (tag) => setBody((b) => b + tag);
  const applyPreset = (p) => {
    setBody(lang === "ar" ? p.body_ar : p.body_en);
    setCampaignName(lang === "ar" ? p.label_ar : p.label_en);
  };

  const segMeta = SEGMENT_META.find((s) => s.id === segment) || SEGMENT_META[0];
  const recipientCount = segments[segment] ?? 0;
  const isStub = status?.mode === "stub";
  const channelLabel = isStub
    ? t("Manual wa.me links — open each conversation in a new tab",
        "روابط wa.me يدوية — يفتح كل محادثة في تبويب جديد")
    : t("Meta WhatsApp Cloud API — sent automatically",
        "Meta WhatsApp Cloud API — إرسال آلي");

  // Open all stub-mode wa.me links in sequence (browsers throttle Window.open
  // so we open them in a fixed cadence).
  const openAllLinks = (results) => {
    let i = 0;
    const links = (results || []).filter((r) => r.wa_link);
    const step = () => {
      if (i >= links.length) return;
      window.open(links[i].wa_link, "_blank", "noopener");
      i += 1;
      setTimeout(step, 400);
    };
    step();
  };

  return (
    <div className="space-y-6" data-testid="whatsapp-campaigns">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
            <MessageCircle size={22} className="text-green-600" />
          </div>
          <div>
            <h1 className="font-heading text-3xl gold-text">
              {t("WhatsApp Campaigns", "حملات واتساب")}
            </h1>
            <p className="text-sm text-[var(--text-muted)]">
              {t("Broadcast tailored messages to customer segments.", "أرسل رسائل مخصصة لشرائح من عملائك.")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowHistory(true)} data-testid="wa-history-btn"
            className="px-3 py-2 rounded-md text-sm border border-[var(--border)] hover:border-[var(--gold)] flex items-center gap-2">
            <History size={14}/>{t("History", "السجل")}
          </button>
          <Link to="/settings" data-testid="wa-go-settings"
            className="px-3 py-2 rounded-md text-sm border border-[var(--border)] hover:border-[var(--gold)] flex items-center gap-2">
            <SettingsIcon size={14}/>{t("Settings", "الإعدادات")}
          </Link>
        </div>
      </div>

      {/* Channel status banner */}
      {status && (
        <div className={`rounded-md p-3 text-sm flex items-center justify-between gap-3 border ${
          isStub ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-green-50 text-green-700 border-green-200"
        }`}>
          <div className="flex items-center gap-2">
            {isStub ? <AlertCircle size={16}/> : <CheckCircle size={16}/>}
            <span><b>{t("Channel", "القناة")}:</b> {channelLabel}</span>
          </div>
          <Link to="/settings" className="text-xs underline">{t("Change channel", "تغيير القناة")}</Link>
        </div>
      )}

      {/* Step 1: pick a segment */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 space-y-3">
        <h2 className="font-heading text-lg">1) {t("Choose your audience", "اختر الجمهور")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {SEGMENT_META.map((s) => {
            const count = segments[s.id] ?? 0;
            const active = segment === s.id;
            const Icon = s.icon;
            return (
              <button key={s.id} onClick={() => { setSegment(s.id); setPreview(null); setResult(null); }}
                data-testid={`seg-${s.id}`}
                className={`text-start p-3 rounded-lg border transition ${active
                  ? "border-[var(--gold)] bg-[var(--gold)]/10 ring-2 ring-[var(--gold)]/30"
                  : "border-[var(--border)] hover:border-[var(--gold)]"
                }`}>
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-8 h-8 rounded-md flex items-center justify-center ${s.color}`}><Icon size={14}/></div>
                  <span className="font-medium">{lang === "ar" ? s.label_ar : s.label_en}</span>
                  <span className="ms-auto text-sm font-bold text-[var(--gold)] num-display">{count}</span>
                </div>
                <p className="text-[11px] text-[var(--text-muted)] leading-relaxed">
                  {lang === "ar" ? s.desc_ar : s.desc_en}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 2: compose */}
      <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-heading text-lg">2) {t("Compose your message", "كتابة الرسالة")}</h2>
          <span className="text-sm text-[var(--text-muted)]">
            {t("Recipients", "المستقبلون")}: <b className="text-[var(--gold)]">{recipientCount}</b>
          </span>
        </div>

        {/* Presets */}
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => applyPreset(p)} data-testid={`preset-${p.id}`}
              className="px-3 py-1.5 text-xs rounded-full border border-[var(--border)] hover:border-[var(--gold)] hover:bg-[var(--gold)]/5">
              {lang === "ar" ? p.label_ar : p.label_en}
            </button>
          ))}
        </div>

        <div>
          <label className="text-xs text-[var(--text-muted)] block mb-1">
            {t("Campaign name (internal)", "اسم الحملة (داخلي)")}
          </label>
          <input className="input-dark" data-testid="campaign-name"
            placeholder={t("e.g. June reactivation", "مثلاً: استرجاع يونيو")}
            value={campaignName} onChange={(e) => setCampaignName(e.target.value)}/>
        </div>

        <div>
          <label className="text-xs text-[var(--text-muted)] block mb-1">
            {t("Message body", "نص الرسالة")} <span className="text-rose-500">*</span>
          </label>
          <textarea rows={5} className="input-dark resize-none" data-testid="campaign-body"
            value={body} onChange={(e) => setBody(e.target.value)}
            placeholder={t("Hi {name}, we miss you...", "مرحباً {name}، نفتقدك...")}/>
          <div className="text-[10px] text-[var(--text-muted)] mt-1 flex justify-between">
            <span>{body.length} {t("chars", "حرف")}</span>
          </div>
        </div>

        <div>
          <div className="text-[11px] text-[var(--text-muted)] mb-1">{t("Insert placeholder", "أدرج متغير")}:</div>
          <div className="flex flex-wrap gap-2">
            {PLACEHOLDERS.map((p) => (
              <button key={p.tag} type="button" onClick={() => insertPlaceholder(p.tag)}
                data-testid={`ph-${p.tag.replace(/[{}]/g, "")}`}
                title={lang === "ar" ? p.desc_ar : p.desc_en}
                className="px-2 py-1 text-[11px] rounded-md border border-[var(--border)] font-mono hover:border-[var(--gold)] hover:text-[var(--gold)]">
                {p.tag}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 flex-wrap pt-2">
          <button onClick={doPreview} disabled={previewing} data-testid="preview-btn"
            className="px-4 py-2 rounded-md border border-[var(--border)] hover:border-[var(--gold)] hover:text-[var(--gold)] flex items-center gap-2">
            <Eye size={14}/>{previewing ? t("...", "...") : t("Preview", "معاينة")}
          </button>
          <button onClick={() => doSend(true)} disabled={sending} data-testid="dryrun-btn"
            className="px-4 py-2 rounded-md border border-[var(--border)] hover:border-[var(--gold)] hover:text-[var(--gold)] flex items-center gap-2">
            <Eye size={14}/>{t("Dry-run", "تجربة جافة")}
          </button>
          <button onClick={() => doSend(false)} disabled={sending || !recipientCount} data-testid="send-btn"
            className="btn-gold px-5 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 ms-auto">
            <Send size={14}/>
            {sending
              ? t("Sending...", "جارٍ الإرسال...")
              : t(`Send to ${recipientCount} customers`, `إرسال إلى ${recipientCount} عميل`)}
          </button>
        </div>
      </div>

      {/* Step 3: preview samples */}
      {preview && (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 space-y-3" data-testid="preview-card">
          <h2 className="font-heading text-lg flex items-center gap-2">
            <Eye size={16}/>{t("Preview samples", "نماذج المعاينة")} ({preview.count} {t("recipients", "مستقبل")})
          </h2>
          {preview.samples.length === 0
            ? <div className="text-sm text-[var(--text-muted)]">{t("No recipients match this segment.", "لا يوجد عملاء يطابقون هذه الشريحة.")}</div>
            : preview.samples.map((sm, i) => (
              <div key={i} className="border border-[var(--border-subtle)] rounded-md p-3 bg-[var(--gold)]/5">
                <div className="text-xs text-[var(--text-muted)] mb-1">
                  <b>{sm.name}</b> · {sm.phone}
                </div>
                <div className="text-sm whitespace-pre-line">{sm.preview}</div>
              </div>
            ))}
        </div>
      )}

      {/* Send results */}
      {result && (
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 space-y-3" data-testid="results-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-heading text-lg flex items-center gap-2">
              <Send size={16}/>
              {result.dry_run ? t("Dry-run results", "نتائج التجربة الجافة") : t("Send results", "نتائج الإرسال")}
            </h2>
            <div className="text-sm">
              <span className="text-green-600 font-bold">{result.success_count}</span>
              <span className="text-[var(--text-muted)]"> / </span>
              <span>{result.total}</span>
              {result.failed_count > 0 && <span className="text-rose-600"> · {result.failed_count} {t("failed", "فشلت")}</span>}
            </div>
          </div>

          {/* For stub mode (manual): open-all helper */}
          {result.mode === "stub" && result.results?.some((r) => r.wa_link) && (
            <button onClick={() => openAllLinks(result.results)} data-testid="open-all-links"
              className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 flex items-center gap-2 text-sm">
              <ExternalLink size={14}/>{t(`Open all ${result.results.length} chats in tabs`, `افتح كل المحادثات (${result.results.length}) في تبويبات`)}
            </button>
          )}

          <div className="max-h-72 overflow-auto border border-[var(--border-subtle)] rounded-md">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--surface)]">
                <tr className="text-[var(--text-muted)] text-start">
                  <th className="p-2 text-start">{t("Customer", "العميل")}</th>
                  <th className="p-2 text-start">{t("Phone", "الجوال")}</th>
                  <th className="p-2 text-start">{t("Status", "الحالة")}</th>
                  <th className="p-2 text-start">{t("Action", "إجراء")}</th>
                </tr>
              </thead>
              <tbody>
                {result.results.map((r, i) => (
                  <tr key={i} className="border-t border-[var(--border-subtle)]">
                    <td className="p-2">{r.name || "—"}</td>
                    <td className="p-2 num-display">{r.phone || "—"}</td>
                    <td className="p-2">
                      {r.success
                        ? <span className="text-green-600 inline-flex items-center gap-1"><CheckCircle size={11}/> {result.mode === "stub" ? t("Link ready", "رابط جاهز") : t("Sent", "أُرسلت")}</span>
                        : <span className="text-rose-600 inline-flex items-center gap-1"><AlertCircle size={11}/> {r.error || "Error"}</span>}
                    </td>
                    <td className="p-2">
                      {r.wa_link && (
                        <a href={r.wa_link} target="_blank" rel="noreferrer"
                          className="text-[var(--gold)] hover:underline inline-flex items-center gap-1">
                          <ExternalLink size={11}/>{t("Open chat", "فتح المحادثة")}
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* History modal */}
      {showHistory && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setShowHistory(false))}>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 w-full max-w-2xl space-y-3 max-h-[90vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
            <div className="flex justify-between">
              <h2 className="font-heading text-xl gold-text">{t("Campaign history", "سجل الحملات")}</h2>
              <button onClick={() => setShowHistory(false)} className="p-1 rounded hover:bg-[var(--gold)]/10"><X size={16}/></button>
            </div>
            {history.length === 0
              ? <div className="text-sm text-[var(--text-muted)]">{t("No campaigns yet.", "لا توجد حملات بعد.")}</div>
              : history.map((c) => (
                <div key={c.campaign_id} className="border border-[var(--border-subtle)] rounded-md p-3 text-sm">
                  <div className="flex justify-between flex-wrap gap-2">
                    <b>{c.name}</b>
                    <span className="text-xs text-[var(--text-muted)] num-display">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
                  </div>
                  <div className="text-xs text-[var(--text-muted)] mt-1">
                    {c.segment} · {c.mode} · <span className="text-green-600">{c.success_count}</span>/{c.total}
                    {c.failed_count > 0 && <span className="text-rose-600"> · {c.failed_count} {t("failed", "فشلت")}</span>}
                  </div>
                  <div className="text-xs mt-2 line-clamp-2 text-[var(--text-muted)]">{c.body}</div>
                </div>
              ))}
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
